// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { constants } from "@hardwoods/XDL/constants";
import { xBaseTheme } from "@hardwoods/XDL/types";
import color from "@hardwoods/XDL/Utilities/color";
import { ButtonProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import get from "@insite/mobius/utilities/get";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not([disabled]) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonSizeVariants = {
    xSmall: { height: 29, fontSize: 9, padding: "0 5px", lineHeight: 11, icon: 12, iconPadding: 5 },
    small: {
        height: 34,
        fontSize: 12,
        padding: "0 10px",
        lineHeight: 22,
        icon: 12,
        iconPadding: 5,
    },
    medium: {
        height: 38,
        width: 129,
        fontSize: 12,
        padding: "0 28px",
        lineHeight: 22,
        icon: 15,
        iconPadding: 7,
    },
    large: {
        height: 50,
        width: 130,
        fontSize: 14,
        lineHeight: 17,
        padding: "0 30px",
        icon: 20,
        iconPadding: 9,
    },
};

const disabledAlpha = 0.33;
export const buttonPresentationStyle = (buttonType: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        color: buttonType,
        buttonType: "solid",
        shape: "rectangle",
        sizeVariant: "medium",
        mergeCss: true,
        css: css`
            border: 1px solid ${color(`${buttonType}.dark`)};
            border-radius: 4px;
            color: ${color("common.white")};
            height: auto;
            backface-visibility: visible; // XNG: To fix OOTB issue
            min-height: ${(props: BWT) =>
                props["_size"] ||
                get(buttonSizeVariants, [props["_sizeVariant"], "height"]) ||
                buttonSizeVariants.medium.height}px;
            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            &:disabled {
                background: ${color(buttonType, disabledAlpha)};
                border-color: transparent; // to remove border-color from OOTB
            }

            &:hover:not([disabled]) {
                background: ${color(`${buttonType}.hover`)};
                border-color: ${color(`${buttonType}.hover`)};
            }

            &:focus:not([disabled]) {
                background: ${color(`${buttonType}.dark`)};
                border-color: ${color(`${buttonType}.dark`)};
            }

            > * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
                letter-spacing: 0.25px;
                line-height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "lineHeight"])}px;
                font-weight: bold;
            }
        `,
    };
};

export default {
    primary: {
        ...buttonPresentationStyle("primary"),
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
    },
    tertiary: {
        ...buttonPresentationStyle("tertiary"),
        css: css`
            ${buttonPresentationStyle("tertiary").css}
            color: ${color("primary")};           
            border: 1px solid ${color("primary")};
            background: ${color("common.white")};

            &:hover:not([disabled]) {
                background: ${color("common.white")};
                border-color: ${color("primary.hover")};
                color: ${color("primary.hover")};
            }

            &:focus:not([disabled]) {
                background: ${color("common.white")};
                border-color: ${color("primary.dark")};
                color: ${color("primary.dark")};
            }
            
            &:disabled {
                background: ${color("common.white", disabledAlpha)};
                color: ${color("primary", disabledAlpha)};
                border-color: ${color("primary", disabledAlpha)};
            }
        `,
    },
} as xBaseTheme["button"];
